import {
  FETCH_PARTNER_ORDERS,
  FETCH_PARTNER_ORDERS_SUCCESS,
  FETCH_PARTNER_ORDERS_FAILED,
  CREATE_PARTNER_ORDER,
  CREATE_PARTNER_ORDER_SUCCESS,
  CREATE_PARTNER_ORDER_FAILED,
  UPDATE_PARTNER_ORDER_STATUS,
  CLEAR_PARTNER_ORDER,
} from '../store/types';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, push, update, get } from 'firebase/database';
import {
  ORDER_STATUS,
  ORDER_TYPES,
  generateOrderReference,
  isStayOrder,
} from '../other/PartnerUtils';

const snapshotToArray = (snap) => {
  const val = snap.val();
  if (!val) return [];
  return Object.keys(val).map((id) => ({ ...val[id], id }));
};

export const fetchPartnerOrdersByCustomer = (uid) => (dispatch) => {
  const { partnerOrdersByCustomerRef } = firebase;
  dispatch({ type: FETCH_PARTNER_ORDERS, payload: null });
  onValue(partnerOrdersByCustomerRef(uid), (snap) => {
    if (snap.val()) {
      dispatch({
        type: FETCH_PARTNER_ORDERS_SUCCESS,
        payload: snapshotToArray(snap),
      });
    } else {
      dispatch({ type: FETCH_PARTNER_ORDERS_SUCCESS, payload: [] });
    }
  });
};

export const fetchPartnerOrdersByPartner = (partnerId) => (dispatch) => {
  const { partnerOrdersByPartnerRef } = firebase;
  dispatch({ type: FETCH_PARTNER_ORDERS, payload: null });
  onValue(partnerOrdersByPartnerRef(partnerId), (snap) => {
    if (snap.val()) {
      dispatch({
        type: FETCH_PARTNER_ORDERS_SUCCESS,
        payload: snapshotToArray(snap),
      });
    } else {
      dispatch({ type: FETCH_PARTNER_ORDERS_SUCCESS, payload: [] });
    }
  });
};

/**
 * Suscripción admin: TODOS los partnerOrders en tiempo real. Solo debe
 * llamarse desde el web-app admin — el rider/partner tiene queries
 * indexadas por uid/partnerId.
 */
export const fetchAllPartnerOrders = () => (dispatch) => {
  const { partnerOrdersRef } = firebase;
  dispatch({ type: FETCH_PARTNER_ORDERS, payload: null });
  onValue(partnerOrdersRef, (snap) => {
    if (snap.val()) {
      dispatch({ type: FETCH_PARTNER_ORDERS_SUCCESS, payload: snapshotToArray(snap) });
    } else {
      dispatch({ type: FETCH_PARTNER_ORDERS_SUCCESS, payload: [] });
    }
  });
};

/**
 * Asigna un conductor a un partnerOrder. Tras esto la function
 * partnerOrderUpdate se encarga de notificar al conductor cuando el order
 * pase a READY.
 */
export const assignDriverToPartnerOrder = (orderId, driverId, driverProfile) => async () => {
  if (!orderId) throw new Error('orderId requerido');
  const { partnerOrderEditRef } = firebase;
  const patch = {
    driver: driverId || null,
    driverName: driverProfile?.firstName ? `${driverProfile.firstName} ${driverProfile.lastName || ''}`.trim() : null,
    driver_token: driverProfile?.pushToken || null,
    assignedAt: driverId ? Date.now() : null,
    updatedAt: Date.now(),
  };
  await update(partnerOrderEditRef(orderId), patch);
};

export const fetchSinglePartnerOrder = (orderId) => async () => {
  const { partnerOrderEditRef } = firebase;
  const snap = await get(partnerOrderEditRef(orderId));
  if (!snap.val()) return null;
  return { ...snap.val(), id: orderId };
};

const buildOrderShell = (order = {}) => {
  const orderType = order.orderType || ORDER_TYPES.STAY;
  const reference = order.reference || generateOrderReference();
  const now = Date.now();
  return {
    customer: order.customer || null,
    customerName: order.customerName || '',
    customerPhone: order.customerPhone || '',
    partnerId: order.partnerId,
    partnerType: order.partnerType,
    partnerName: order.partnerName || '',
    partnerCover: order.partnerCover || null,
    orderType,
    status: order.status || ORDER_STATUS.PENDING,
    statusHistory: [
      {
        status: order.status || ORDER_STATUS.PENDING,
        at: now,
        by: order.customer || 'system',
      },
    ],
    zoneId: order.zoneId || null,
    currency: order.currency || 'USD',
    subtotal: order.subtotal || 0,
    tax: order.tax || 0,
    fees: order.fees || 0,
    deliveryFee: order.deliveryFee || 0,
    discount: order.discount || 0,
    total: order.total || 0,
    payment_mode: order.payment_mode || 'cash',
    paymentPacket: order.paymentPacket || null,
    paid: !!order.paid,
    paymentRef: order.paymentRef || null,
    promoCode: order.promoCode || null,
    promoDiscount: order.promoDiscount || 0,
    notes: order.notes || '',
    stay: orderType === ORDER_TYPES.STAY ? order.stay || null : null,
    purchase: orderType === ORDER_TYPES.PURCHASE ? order.purchase || null : null,
    // Fase 15: si el socio opera en auto-servicio, el conductor hace la compra.
    // El cliente / rider lo setea al checkout copiando el flag del partner.
    selfService: !!order.selfService,
    linkedBookingId: null,
    reference,
    createdAt: now,
    updatedAt: now,
  };
};

export const createPartnerOrder = (orderInput) => async (dispatch) => {
  const { partnerOrdersRef, partnerOrderEditRef } = firebase;
  const order = buildOrderShell(orderInput);
  dispatch({ type: CREATE_PARTNER_ORDER, payload: order });
  try {
    const ref = await push(partnerOrdersRef, order);
    const orderId = ref.key;
    // Make id available on read
    await update(partnerOrderEditRef(orderId), { id: orderId });
    dispatch({
      type: CREATE_PARTNER_ORDER_SUCCESS,
      payload: { orderId, order: { ...order, id: orderId } },
    });
    return orderId;
  } catch (err) {
    dispatch({
      type: CREATE_PARTNER_ORDER_FAILED,
      payload: err && err.message ? err.message : 'Failed to create order',
    });
    throw err;
  }
};

export const updatePartnerOrderStatus = (orderId, nextStatus, byUid) => async (dispatch) => {
  const { partnerOrderEditRef } = firebase;
  dispatch({ type: UPDATE_PARTNER_ORDER_STATUS, payload: { orderId, nextStatus } });
  const snap = await get(partnerOrderEditRef(orderId));
  if (!snap.val()) return null;
  const prev = snap.val();
  const history = Array.isArray(prev.statusHistory) ? prev.statusHistory : [];
  await update(partnerOrderEditRef(orderId), {
    status: nextStatus,
    statusHistory: [
      ...history,
      { status: nextStatus, at: Date.now(), by: byUid || 'system' },
    ],
    updatedAt: Date.now(),
  });
  return nextStatus;
};

export const cancelPartnerOrder = (orderId, byUid, reason) => async (dispatch) => {
  const { partnerOrderEditRef } = firebase;
  dispatch({ type: UPDATE_PARTNER_ORDER_STATUS, payload: { orderId, nextStatus: ORDER_STATUS.CANCELLED } });
  const snap = await get(partnerOrderEditRef(orderId));
  if (!snap.val()) return null;
  const prev = snap.val();
  const history = Array.isArray(prev.statusHistory) ? prev.statusHistory : [];
  await update(partnerOrderEditRef(orderId), {
    status: ORDER_STATUS.CANCELLED,
    cancellationReason: reason || null,
    cancelledBy: byUid || null,
    statusHistory: [
      ...history,
      { status: ORDER_STATUS.CANCELLED, at: Date.now(), by: byUid || 'system' },
    ],
    updatedAt: Date.now(),
  });
  return ORDER_STATUS.CANCELLED;
};

export const attachLinkedBooking = (orderId, bookingId) => async () => {
  const db = firebase.database;
  const { ref, update: rtdbUpdate } = await import('firebase/database');
  await rtdbUpdate(ref(db), {
    [`partnerOrders/${orderId}/linkedBookingId`]: bookingId,
    [`bookings/${bookingId}/linkedOrderId`]: orderId,
  });
};

export const clearPartnerOrder = () => (dispatch) => {
  dispatch({ type: CLEAR_PARTNER_ORDER });
};

export const isOrderStay = (order) => isStayOrder(order);

// utility selector for callers without redux access
export const getPartnerOrderState = () => store.getState().partnerorderdata;
